import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import { imagepathReturn, formatDate } from "../../config/formatters.ts";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface EventItem {
  id: number;
  title: string;
  label: string;
  thumbnail_image: string;
  event_date: string;
  event_time: string;
  event_location: string;
  sort_order: number;
  status: boolean;
}

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents();
  }, [page, limit, search]);

  const getEvents = async () => {
    setLoading(true);
    const response = await request.post("events", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setEvents(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "thumbnail_image",
      label: "Image",
      minWidth: 80,
      format: (value) => (
        <Box
          component="img"
          src={imagepathReturn(value)}
          sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: "8px" }}
        />
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "label",
      label: "Label",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "event_date",
      label: "Event Date",
      minWidth: 140,
      format: (value) => (value ? formatDate(value) : "—"),
    },
    {
      id: "event_location",
      label: "Location",
      minWidth: 150,
      format: (value) => value || "—",
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/events/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Events"
        showSearch
        searchPlaceholder="Search events..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Event",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/events/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={events}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Event - " + deleteDialog?.title}
          message="Are you sure you want to delete this event? This action cannot be undone."
          apiEndpoint={"events/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getEvents();
          }}
        />
      </PageWrapper>
    </>
  );
}